import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { Button } from '@domcrm/embed-ui';

import {
  fetchPublicOrganizationsWithBranches,
  type PublicOrganizationWithBranchesItem
} from '../widget-api/public-widget';
import { useEmbedBootstrap } from '../use-widget-bootstrap';
import { useBranchLabels } from '../widget-hooks/useBranchLabels';
import { useAsync } from '../widget-hooks/useAsync';
import { useWidgetSeo } from '../widget-hooks/use-widget-seo';
import { PATH } from '../widget-lib/path-routes';
import { setLastGuestOrganizationId } from '../widget-lib/embed-user-prefs';
import { buildBranchLabelCases, formatBranchCount } from '../widget-lib/ru-branch-label-cases';
import { plainTextSnippet } from '../widget-lib/plain-text-snippet';
import { displayableMediaUrl } from '../widget-lib/media-url';
import { MediaAvatar } from '../widget-ui/media-avatar';

/**
 * Витрина глобального виджета: каталог студий из публичного API (без id/slug в карточках).
 */
export default function GlobalWidgetLandingPage() {
  useWidgetSeo(
    'Каталог организаций',
    'Каталог студий, клубов и центров для онлайн-записи на тренировки.'
  );
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const qs = searchParams.toString();
  const suffix = qs ? `?${qs}` : '';
  const { globalWidgetConfig, accessToken, loading: globalLoading, error: globalError, reload } = useEmbedBootstrap();
  const branchLabels = useBranchLabels();
  const title = globalWidgetConfig?.title?.trim() || 'Запись на занятие';

  const { data: orgs, loading: orgsLoading, error: orgsError, reload: reloadOrgs } = useAsync(
    () => fetchPublicOrganizationsWithBranches(),
    []
  );

  const loading = globalLoading || orgsLoading;
  const error = globalError ?? orgsError;

  function goToOrg(org: PublicOrganizationWithBranchesItem) {
    setLastGuestOrganizationId(org.id);
    const next = new URLSearchParams(searchParams);
    next.set('embed_global', '1');
    next.set('guest_org_id', String(org.id));
    navigate({ pathname: PATH.home, search: next.toString() ? `?${next.toString()}` : '' });
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="space-y-2 w-full min-w-0">
          <div className="h-6 w-48 animate-pulse rounded bg-[var(--bg-soft)]" />
          <div className="h-4 w-full animate-pulse rounded bg-[var(--bg-soft)]" />
          <div className="h-3 w-40 animate-pulse rounded bg-[var(--bg-soft)]" />
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <OrgCardSkeleton key={i} />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-3 w-full min-w-0">
        <p className="text-sm text-[var(--error-text)]">{error}</p>
        <button
          type="button"
          className="text-sm text-[var(--accent)] underline-offset-2 hover:underline"
          onClick={() => {
            reload();
            reloadOrgs();
          }}
        >
          Повторить
        </button>
      </div>
    );
  }

  const list = (orgs ?? []).slice().sort((a, b) => a.name.localeCompare(b.name, 'ru'));

  return (
    <div className="space-y-6">
      <div className="space-y-2 w-full min-w-0">
        <h1 className="text-xl font-semibold tracking-tight sm:text-2xl">{title}</h1>
        <p className="text-sm text-[var(--text-muted)]">
          Выберите студию. Расписание и {branchLabels.pluralLc} можно смотреть без входа; запись и оплата — после
          авторизации.
        </p>
        {!accessToken ? (
          <p className="text-xs text-[var(--text-muted)]">
            <Link className="font-medium text-[var(--accent)] underline-offset-2 hover:underline" to={`${PATH.signIn}${suffix}`}>
              Вход
            </Link>
          </p>
        ) : (
          <p className="text-xs text-[var(--text-muted)]">
            Вы вошли в систему.{' '}
            <Link className="font-medium text-[var(--accent)] underline-offset-2 hover:underline" to={`${PATH.account}${suffix}`}>
              Личный кабинет
            </Link>
            {' · '}
            <Link className="font-medium text-[var(--accent)] underline-offset-2 hover:underline" to={`${PATH.bookings}${suffix}`}>
              Мои записи
            </Link>
            {' · '}
            <Link className="font-medium text-[var(--accent)] underline-offset-2 hover:underline" to={`${PATH.mySubscriptions}${suffix}`}>
              Мои абонементы
            </Link>
          </p>
        )}
      </div>

      {list.length === 0 ? (
        <div className="rounded-xl border border-[var(--border)] bg-[var(--bg-elevated)] p-8 text-center shadow-sm">
          <p className="text-sm text-[var(--text-muted)]">Пока нет студий в каталоге.</p>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          {list.map((org) => (
            <OrgShowcaseCard key={org.id} org={org} onOpen={() => goToOrg(org)} />
          ))}
        </div>
      )}
    </div>
  );
}

function OrgCardSkeleton() {
  return (
    <div className="flex flex-col overflow-hidden rounded-xl border border-[var(--border)] bg-[var(--bg-elevated)]">
      <div className="h-28 animate-pulse bg-[var(--bg-soft)] sm:h-32" />
      <div className="flex flex-1 flex-col gap-3 p-4">
        <div className="flex items-start gap-3">
          <div className="-mt-8 h-11 w-11 shrink-0 animate-pulse rounded-full bg-[var(--bg-soft)] ring-2 ring-[var(--bg-elevated)] sm:h-12 sm:w-12" />
          <div className="flex-1 space-y-2 pt-0.5">
            <div className="h-4 w-3/4 animate-pulse rounded bg-[var(--bg-soft)]" />
            <div className="h-3 w-1/2 animate-pulse rounded bg-[var(--bg-soft)]" />
          </div>
        </div>
        <div className="space-y-1.5">
          <div className="h-3 w-full animate-pulse rounded bg-[var(--bg-soft)]" />
          <div className="h-3 w-4/5 animate-pulse rounded bg-[var(--bg-soft)]" />
        </div>
        <div className="mt-auto h-9 w-full animate-pulse rounded-xl bg-[var(--bg-soft)]" />
      </div>
    </div>
  );
}

function OrgShowcaseCard({
  org,
  onOpen
}: {
  org: PublicOrganizationWithBranchesItem;
  onOpen: () => void;
}) {
  const name = (org.name || '').trim() || 'Студия';
  const snippet = plainTextSnippet(org.description, 140);
  const hero = displayableMediaUrl(org.photo_url);
  const showHero = hero != null;
  const typeLabel = (org.org_type ?? '').trim();
  const multi = org.branches.length > 1;
  const plural = (org.branch_label_plural ?? '').trim() || 'Салоны';
  const singular = (org.branch_label_singular ?? '').trim() || 'Салон';
  const labelCases = buildBranchLabelCases(plural, singular);

  return (
    <article className="flex flex-col overflow-hidden rounded-xl border border-[var(--border)] bg-[var(--bg-elevated)] shadow-sm">
      {showHero ? (
        <div className="relative h-28 bg-black sm:h-32">
          <img
            src={hero}
            alt=""
            className="absolute inset-0 h-full w-full object-cover"
            sizes="(max-width: 640px) 100vw, 28rem"
            decoding="async"
            loading="lazy"
          />
          <div
            className="pointer-events-none absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-[var(--bg-elevated)] to-transparent"
            aria-hidden
          />
        </div>
      ) : null}
      <div className="flex flex-1 flex-col gap-3 p-4">
        <div className="flex min-w-0 items-start gap-3">
          <MediaAvatar
            photoUrl={org.brand_logo_url}
            label={name}
            sizeClass={`relative z-10 h-11 w-11 shrink-0 sm:h-12 sm:w-12 ${showHero ? '-mt-8' : 'mt-0'}`}
            roundedClass="rounded-full ring-2 ring-[var(--bg-elevated)]"
            imgSizes="(max-width: 640px) 44px, 48px"
          />
          <div className="min-w-0 flex-1 pt-0.5">
            <h2 className="text-base font-semibold leading-snug text-[var(--text)]">{name}</h2>
            <div className="mt-1 flex flex-wrap gap-1.5">
              {typeLabel ? (
                <span className="inline-flex rounded-md border border-[var(--border)] bg-[var(--bg-soft)] px-2 py-0.5 text-[11px] font-medium text-[var(--text-muted)]">
                  {typeLabel}
                </span>
              ) : null}
              <span className="inline-flex rounded-md border border-[var(--border)] bg-[var(--bg-soft)] px-2 py-0.5 text-[11px] font-medium text-[var(--text)]">
                {multi ? `Несколько ${labelCases.genitivePluralLc}` : formatBranchCount(org.branches.length, labelCases)}
              </span>
            </div>
          </div>
        </div>
        {snippet ? <p className="text-sm leading-relaxed text-[var(--text-muted)]">{snippet}</p> : null}
        {multi && org.branches.length > 0 ? (
          <ul className="text-xs text-[var(--text-muted)]">
            {org.branches.slice(0, 3).map((b) => (
              <li key={b.id} className="truncate">
                {b.name}
                {b.address ? ` — ${b.address}` : null}
              </li>
            ))}
            {org.branches.length > 3 ? <li className="text-[var(--text-muted)]">…</li> : null}
          </ul>
        ) : null}
        <Button type="button" className="mt-auto w-full" onClick={onOpen}>
          Открыть
        </Button>
      </div>
    </article>
  );
}
